import { StrictMode, Suspense, useEffect, type ReactNode } from "react";
import { lazyPage, BootSplash } from "./ui/lazyPage";
import { AppBoundary } from "./ui/ErrorBoundary";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { authEnabled, publishableKey, PlayRoute, AuthAnalytics } from "./ui/AuthGate";
import { Landing } from "./ui/site/Landing";
import { NotFound } from "./ui/site/NotFound";
import { PrelaunchGate } from "./ui/site/PrelaunchGate";
import { shotSave } from "./sim/shotMode";
import { trackPageView } from "./sim/analytics";
import { installErrorReports } from "./sim/clientHealth";
// CODE-SPLIT ROUTES. Only the landing page is in the entry chunk; every other page — and the game itself,
// behind PlayRoute — loads on first visit to its route. Before this the whole site, the admin console, the
// collision editor and the game shipped as one 2.9 MB script to whoever opened "/".
const WhatsNew = lazyPage(() => import("./ui/site/WhatsNew").then((m) => ({ default: m.WhatsNew })));
const Shop = lazyPage(() => import("./ui/site/Shop").then((m) => ({ default: m.Shop })));
const Profile = lazyPage(() => import("./ui/site/Profile").then((m) => ({ default: m.Profile })));
const RedeemPage = lazyPage(() => import("./ui/site/RedeemPage").then((m) => ({ default: m.RedeemPage })));
const GardenClub = lazyPage(() => import("./ui/site/GardenClub").then((m) => ({ default: m.GardenClub })));
const AdminOverviewPage = lazyPage(() => import("./ui/site/AdminHome").then((m) => ({ default: m.AdminOverviewPage })));
const AdminSettingsPage = lazyPage(() => import("./ui/site/AdminHome").then((m) => ({ default: m.AdminSettingsPage })));
const AdminBroadcastPage = lazyPage(() => import("./ui/site/AdminHome").then((m) => ({ default: m.AdminBroadcastPage })));
const AdminKickstarterPage = lazyPage(() => import("./ui/site/AdminHome").then((m) => ({ default: m.AdminKickstarterPage })));
const AdminTuningPage = lazyPage(() => import("./ui/site/AdminHome").then((m) => ({ default: m.AdminTuningPage })));
const AdminPlayersPage = lazyPage(() => import("./ui/site/AdminPage").then((m) => ({ default: m.AdminPlayersPage })));
const AdminPlayerPage = lazyPage(() => import("./ui/site/AdminPage").then((m) => ({ default: m.AdminPlayerPage })));
const AdminStatsPage = lazyPage(() => import("./ui/site/AdminPage").then((m) => ({ default: m.AdminStatsPage })));
const AdminAuditPage = lazyPage(() => import("./ui/site/AdminPage").then((m) => ({ default: m.AdminAuditPage })));
const AdminAnalyticsOverview = lazyPage(() => import("./ui/site/AdminAnalytics").then((m) => ({ default: m.AdminAnalyticsOverview })));
const AdminAnalyticsLive = lazyPage(() => import("./ui/site/AdminAnalytics").then((m) => ({ default: m.AdminAnalyticsLive })));
const AdminAnalyticsVisitors = lazyPage(() => import("./ui/site/AdminVisitors").then((m) => ({ default: m.AdminAnalyticsVisitors })));
const AdminAnalyticsRevenue = lazyPage(() => import("./ui/site/AdminRevenue").then((m) => ({ default: m.AdminAnalyticsRevenue })));
const AdminAnalyticsEconomy = lazyPage(() => import("./ui/site/AdminEconomy").then((m) => ({ default: m.AdminAnalyticsEconomy })));
const AdminAnalyticsEngagement = lazyPage(() => import("./ui/site/AdminEngagement").then((m) => ({ default: m.AdminAnalyticsEngagement })));
const AdminAnalyticsHealth = lazyPage(() => import("./ui/site/AdminHealth").then((m) => ({ default: m.AdminAnalyticsHealth })));
const AdminProducts = lazyPage(() => import("./ui/site/AdminProducts").then((m) => ({ default: m.AdminProducts })));
const AdminCodes = lazyPage(() => import("./ui/site/AdminCodes").then((m) => ({ default: m.AdminCodes })));
const AdminWhatsNew = lazyPage(() => import("./ui/site/AdminWhatsNew").then((m) => ({ default: m.AdminWhatsNew })));
const AdminPrelaunch = lazyPage(() => import("./ui/site/AdminPrelaunch").then((m) => ({ default: m.AdminPrelaunch })));
// the old "Player account" page, merged into the player page — its addresses redirect there
const AdminAccountRedirect = lazyPage(() => import("./ui/site/AdminAccount").then((m) => ({ default: m.AdminAccountRedirect })));
const AdminPages = lazyPage(() => import("./ui/site/AdminPages").then((m) => ({ default: m.AdminPages })));
const CollisionEditorPage = lazyPage(() => import("./ui/site/CollisionEditorPage").then((m) => ({ default: m.CollisionEditorPage })));
const AdminTheme = lazyPage(() => import("./ui/site/AdminTheme").then((m) => ({ default: m.AdminTheme })));
const OfflineSceneView = lazyPage(() => import("./ui/site/OfflineSceneView").then((m) => ({ default: m.OfflineSceneView })));
const Contact = lazyPage(() => import("./ui/site/LegalPages").then((m) => ({ default: m.Contact })));
const Terms = lazyPage(() => import("./ui/site/LegalPages").then((m) => ({ default: m.Terms })));
const Refunds = lazyPage(() => import("./ui/site/LegalPages").then((m) => ({ default: m.Refunds })));
const Privacy = lazyPage(() => import("./ui/site/LegalPages").then((m) => ({ default: m.Privacy })));
import { EnvBanner } from "./ui/EnvBanner";
import { startTuningSync, tuningKey, onTuning } from "./sim/tuningClient";
import { startSiteTheme } from "./sim/siteTheme";
import { startGameTheme } from "./sim/gameTheme";
import { setInsectConditionOverrides, setSpawnConfig, setInsectRarityOverrides, type InsectConditionOverrides, type SpawnConfig, type InsectRarityOverrides } from "./sim/insects";
import { setFishConditionOverrides, setFishSpawnConfig, setFishRarityOverrides, setFishValueOverrides, type FishConditionOverrides, type FishSpawnConfig, type FishRarityOverrides, type FishValueOverrides } from "./sim/fishSpawn";
import { setCheckinRewards, type CheckinRewards } from "./sim/checkin";
import { setAchievementOverrides } from "./sim/achievements";
import { setBadgeStyleOverrides } from "./sim/badges";
import { setPointTrackOverrides } from "./sim/pointTrack";
import { registerAchievementGenerator } from "./sim/achievements";
import { generatedAchievements } from "./sim/achievementFamilies";
import { setDecorPriceOverrides, type DecorPriceOverrides } from "./sim/decorPricing";
import "./sim/cosmeticNames";   // display names for skins, species and trophy keys in reward/progress text
import "./ui/styles.css";
import { AdminReleases } from "./ui/site/AdminReleases";
import { AdminBackups } from "./ui/site/AdminBackups";

// Live tuning + themes: fetch the server tuning blob at boot (and poll), then apply the admin-set SITE theme
// (colors + fixed background pattern) and the in-GAME UI theme over the source defaults, re-applying on change.
// Uncaught errors and unhandled rejections → one analytics report per distinct error (sim/clientHealth.ts).
installErrorReports();
startTuningSync();
startSiteTheme();
startGameTheme();
// Admin-authored per-insect spawn-condition overrides (Insect Odds modal → `insectConditions` tuning key).
const applyInsectConds = () => {
  setInsectConditionOverrides(tuningKey<InsectConditionOverrides>("insectConditions", {}));
  setSpawnConfig(tuningKey<Partial<SpawnConfig>>("insectSpawn", {}));
  setInsectRarityOverrides(tuningKey<InsectRarityOverrides>("insectRarity", {}));
  setFishConditionOverrides(tuningKey<FishConditionOverrides>("fishConditions", {}));
  setFishSpawnConfig(tuningKey<Partial<FishSpawnConfig>>("fishSpawn", {}));
  setFishRarityOverrides(tuningKey<FishRarityOverrides>("fishRarity", {}));
  setFishValueOverrides(tuningKey<FishValueOverrides>("fishValue", {}));
  setCheckinRewards(tuningKey<Partial<CheckinRewards>>("checkinRewards", {}));
  setDecorPriceOverrides(tuningKey<DecorPriceOverrides>("decorPrices", {}));
  // ACHIEVEMENTS + BADGES, authored in the dev menu's Achievements editor. Both are per-id overlays merged over
  // the source defaults, so an edit to one achievement or badge never disturbs the rest of the ladder.
  setAchievementOverrides(tuningKey<Record<string, unknown>>("achievements", {}));
  setBadgeStyleOverrides(tuningKey<Record<string, unknown>>("badgeStyles", {}));
  // THE POINT REWARD TRACK. Unlike the two above this key is the WHOLE ordered milestone list rather than a
  // per-id overlay — see the header of sim/pointTrack.ts. `undefined` (key unset) is passed through on
  // purpose: the installer treats anything it cannot read as "keep the source defaults", so a player never
  // sees an empty track.
  setPointTrackOverrides(tuningKey<unknown>("pointTrack", undefined));
};
// The per-plant harvest ladder (thousands of achievements) is GENERATED from a rule rather than stored — see
// sim/achievementFamilies.ts. Registered before the first tuning apply so live overrides land on top of it.
registerAchievementGenerator(generatedAchievements);
applyInsectConds();
onTuning(applyInsectConds);
// DEV: expose the icon bakers to scripts/bake-icons.mjs (never in the production bundle)
if (import.meta.env.DEV) void import("./dev/iconBake");
// DEV: expose a per-species plant BAKE TIMING through the real scene path to scripts/baketime.mjs
if (import.meta.env.DEV) void import("./dev/bakeTime");
// DEV: expose the Fish Odds row round-trip check to scripts/smoke.mjs (never in the production bundle)
if (import.meta.env.DEV) void import("./dev/fishCondRoundTrip");
// DEV: the same check for the Insect Odds rows (weather was missing there too)
if (import.meta.env.DEV) void import("./dev/insectCondRoundTrip");
// DEV: log every live-tuning entry that REPLACES authored data (every key installed above, plus decor bands) — see dev/overrideReport
if (import.meta.env.DEV) void import("./dev/overrideReport");

/**
 * App shell: a marketing SITE at "/" with the game at "/play".
 *   /          → Landing (public)
 *   /whats-new → news/changelog
 *   /shop      → premium shop (placeholder)
 *   /admin     → admin console (admins only; server-gated): a Dashboard, then a tab per tool grouped Players ·
 *                Analytics · Rewards & Codes · Store · Game Tuning · Site · System (site/AdminShell ADMIN_NAV). Unknown
 *                /admin/* paths land on the Dashboard rather than the marketing page.
 *   /play      → the game (sign-in required when auth is enabled)
 *
 * Clerk wraps the whole router so every page can read the session (the site nav
 * shows Sign in / account accordingly). When no Clerk key is present (local dev /
 * plain build), Clerk is skipped and the game plays anonymously.
 */
function Routed() {
  return (
    <BrowserRouter>
      <PageViews />
      <EnvBanner />
      {/* lazy routes resolve here; the game has its own boot splash inside GameLoader */}
      <Suspense fallback={<BootSplash />}>
      {/* PRE-LAUNCH MODE: while it is on, every address shows the one landing page to everyone but admins */}
      <PrelaunchGate>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/whats-new" element={<WhatsNew />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/redeem" element={<RedeemPage />} />
        <Route path="/garden-club" element={<GardenClub />} />
        <Route path="/admin" element={<AdminOverviewPage />} />
        <Route path="/admin/players" element={<AdminPlayersPage />} />
        <Route path="/admin/players/:id" element={<AdminPlayerPage />} />
        <Route path="/admin/account" element={<AdminAccountRedirect />} />
        <Route path="/admin/account/:id" element={<AdminAccountRedirect />} />
        <Route path="/admin/stats" element={<AdminStatsPage />} />
        <Route path="/admin/audit" element={<AdminAuditPage />} />
        <Route path="/admin/analytics" element={<AdminAnalyticsOverview />} />
        <Route path="/admin/analytics/live" element={<AdminAnalyticsLive />} />
        <Route path="/admin/analytics/visitors" element={<AdminAnalyticsVisitors />} />
        <Route path="/admin/analytics/revenue" element={<AdminAnalyticsRevenue />} />
        <Route path="/admin/analytics/economy" element={<AdminAnalyticsEconomy />} />
        <Route path="/admin/analytics/engagement" element={<AdminAnalyticsEngagement />} />
        <Route path="/admin/analytics/health" element={<AdminAnalyticsHealth />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        <Route path="/admin/kickstarter" element={<AdminKickstarterPage />} />
        <Route path="/admin/releases" element={<AdminReleases />} />
        <Route path="/admin/backups" element={<AdminBackups />} />
        <Route path="/admin/tuning" element={<AdminTuningPage />} />
        <Route path="/admin/broadcast" element={<AdminBroadcastPage />} />
        <Route path="/admin/products" element={<AdminProducts />} />
        <Route path="/admin/codes" element={<AdminCodes />} />
        <Route path="/admin/whats-new" element={<AdminWhatsNew />} />
        <Route path="/admin/prelaunch" element={<AdminPrelaunch />} />
        <Route path="/admin/pages" element={<AdminPages />} />
        <Route path="/admin/collisions" element={<CollisionEditorPage />} />
        <Route path="/admin/theme" element={<AdminTheme />} />
        {import.meta.env.DEV && <Route path="/admin/offline-preview" element={<OfflineViewPreview />} />}
        <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/play" element={<PlayRoute />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </PrelaunchGate>
      </Suspense>
    </BrowserRouter>
  );
}

/** One analytics `page_view` per route shown — the site's pages and the game's /play alike (docs/ANALYTICS.md). */
function PageViews() {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
}

/** DEV-ONLY: preview the admin OfflineSceneView with the screenshot preset save (no auth/backend needed). */
function OfflineViewPreview() {
  const save = shotSave() ?? undefined;
  if (!save) return <div style={{ color: "#fff", padding: 40 }}>no preset save</div>;
  return <OfflineSceneView playerId="preview-player" initialSave={save} onClose={() => { window.location.href = "/admin"; }} />;
}

function Root({ children }: { children: ReactNode }) {
  if (authEnabled) {
    return (
      <ClerkProvider publishableKey={publishableKey!} afterSignOutUrl="/">
        <AuthAnalytics />
        {children}
      </ClerkProvider>
    );
  }
  return <>{children}</>;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root>
      {/* the last line of defence: a crash anywhere shows a Reload card, never an empty page */}
      <AppBoundary>
        <Routed />
      </AppBoundary>
    </Root>
  </StrictMode>,
);
